import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const REASON_LINKS = {
  WAIVER_INVALID: '/app/bookings',
  RESERVE_MISSING: '/app/documents',
  RESERVE_INVALID: '/app/documents',
};

const EligibilityPanel = ({ eligibility }) => {
  const { t } = useTranslation();

  if (!eligibility) return null;

  const { eligible, reasons = [], profile = {} } = eligibility;

  const failingCodes = new Set(reasons.map(r => r.code));

  const checks = [
    { label: t('funJumper.eligibility.waiver'), ok: !failingCodes.has('WAIVER_INVALID') && !failingCodes.has('DISCIPLINE_WAIVER_MISSING'), codes: ['WAIVER_INVALID', 'DISCIPLINE_WAIVER_MISSING'] },
    { label: t('funJumper.eligibility.license'), ok: !failingCodes.has('LICENSE_UNVERIFIED') && !failingCodes.has('LICENSE_EXPIRED') && !failingCodes.has('LICENSE_ORG_INVALID'), codes: ['LICENSE_UNVERIFIED', 'LICENSE_EXPIRED', 'LICENSE_ORG_INVALID'] },
    { label: t('funJumper.eligibility.reserve'), ok: !failingCodes.has('RESERVE_MISSING') && !failingCodes.has('RESERVE_INVALID'), codes: ['RESERVE_MISSING', 'RESERVE_INVALID'] },
    { label: t('funJumper.eligibility.briefing'), ok: !failingCodes.has('BRIEFING_MISSING') && !failingCodes.has('BRIEFING_EXPIRED'), codes: ['BRIEFING_MISSING', 'BRIEFING_EXPIRED'] },
    { label: t('funJumper.eligibility.weight'), ok: !failingCodes.has('WEIGHT_MISSING') && !failingCodes.has('WEIGHT_VERIFICATION_EXPIRED') && !failingCodes.has('WEIGHT_EXCEEDS_LIMIT'), codes: ['WEIGHT_MISSING', 'WEIGHT_VERIFICATION_EXPIRED', 'WEIGHT_EXCEEDS_LIMIT'] },
    { label: t('funJumper.eligibility.medical'), ok: !failingCodes.has('MEDICAL_BLOCKED'), codes: ['MEDICAL_BLOCKED'] },
  ];

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('funJumper.eligibility.title')}</h3>
        {eligible ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">{t('funJumper.eligibility.cleared')}</Badge>
        ) : (
          <Badge variant="destructive">{t('funJumper.eligibility.blocked')}</Badge>
        )}
      </div>

      <div className="grid gap-2">
        {checks.map((check) => {
          const failingReason = reasons.find(r => check.codes.includes(r.code));
          return (
            <div key={check.label} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                {check.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
                <span className={cn(check.ok ? 'text-muted-foreground' : 'text-foreground font-medium')}>
                  {check.label}
                </span>
              </div>
              {!check.ok && failingReason && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-600">
                    {t(`funJumper.eligibility.reasons.${failingReason.code}`)}
                  </span>
                  {REASON_LINKS[failingReason.code] ? (
                    <Link
                      to={REASON_LINKS[failingReason.code]}
                      className="text-xs text-blue-600 hover:underline font-medium"
                    >
                      {t(`funJumper.eligibility.fixActions.${failingReason.code}`)}
                    </Link>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {t(`funJumper.eligibility.fixActions.${failingReason.code}`)}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {profile.licenseType && (
        <div className="flex items-center gap-3 pt-2 border-t text-xs text-muted-foreground">
          <span>{t('funJumper.eligibility.licenseLabel')}: {profile.licenseType} ({profile.licenseIssuingBody})</span>
          {profile.rentingEquipment ? (
            <Badge variant="outline" className="text-[10px]">{t('funJumper.eligibility.rentingGear')}</Badge>
          ) : (
            <Badge variant="outline" className="text-[10px]">{t('funJumper.eligibility.ownEquipment')}</Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default EligibilityPanel;
